import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Box, CircularProgress, Dialog, Typography } from '@mui/material'
import { useMsal } from '@azure/msal-react'
import { LOCAL_STORAGE_KEYS } from '@src/utils/constants'
import modalStyles from '@components/modal/modal.module.scss'
import LxCancelButton from '@src/components/cancelButton/LxCancelButton'
import LxSaveButton from '@src/components/saveButton/LxSaveButton'
import { checkAccSettingsScope } from '@src/utils/accountSettings'

const THROTTLE_INTERVAL = 2000 // throttle time
const COUNTDOWN_INTERVAL = 1000
const ONE_MIN = 60 * 1000
const FIVE_MIN = 5 * 60 * 1000

interface SessionTimeoutProps {
  userId: string
}

const SessionTimeout = ({ userId }: SessionTimeoutProps) => {
  // Create user-specific activity key
  const LAST_ACTIVITY_KEY = `lastActivity-${userId}`

  // Check if the user has access to the screen timeout setting
  const { canAccess: hasAccessToTimeOut, effectiveValue } = checkAccSettingsScope('screen_timeout')
  const parsed = Number(effectiveValue)
  const timeoutInMs = hasAccessToTimeOut && !isNaN(parsed) && parsed >= 1 ? parsed * 60 * 1000 : null

  const { instance } = useMsal()
  const [open, setOpen] = useState(false)
  const [remainingTime, setRemainingTime] = useState(0)

  // Consolidated refs
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const isCountdownActiveRef = useRef(false)
  const throttleRef = useRef<NodeJS.Timeout | null>(null)

  // Derive dynamic warning window W and whether we should warn at all
  // If T=0 → inactivity logout disabled.
  // If T=1 minute → no warning, auto-logout at 1 min.
  // If T≥2 → show warning at T − W, where W = min(5m, max(1m, floor(T/2))).
  // (i.e., warn halfway to expiry, capped between 1 and 5 minutes.)
  const { warningTimeMs, shouldWarn } = useMemo(() => {
    if (timeoutInMs == null) return { warningTimeMs: 0, shouldWarn: false }
    if (timeoutInMs === 0) return { warningTimeMs: 0, shouldWarn: false } // T=0 → disabled
    if (timeoutInMs < 2 * ONE_MIN) return { warningTimeMs: 0, shouldWarn: false } // T<2m → no warning
    const W = Math.min(FIVE_MIN, Math.max(ONE_MIN, Math.floor(timeoutInMs / 2)))
    return { warningTimeMs: W, shouldWarn: true }
  }, [timeoutInMs])

  // Memoized constants
  const activityEvents = useMemo(() => ['mousedown', 'keydown', 'scroll', 'touchstart'] as const, [])

  const clearAllTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (throttleRef.current) {
      clearTimeout(throttleRef.current)
      throttleRef.current = null
    }
    isCountdownActiveRef.current = false
  }, [])

  const logout = useCallback(() => {
    clearAllTimers()
    LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
    localStorage.removeItem(LAST_ACTIVITY_KEY)
    sessionStorage.removeItem('msal.interaction.status')
    setOpen(false)
    localStorage.setItem('user_logged_out', Date.now().toString())  // for cross-tab logout handling
    instance.logoutRedirect()
  }, [instance, clearAllTimers, LAST_ACTIVITY_KEY])

  const updateActivity = useCallback(() => {
    const now = Date.now()
    lastActivityRef.current = now
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString())
  }, [LAST_ACTIVITY_KEY])

  const startCountdown = useCallback(() => {
    if (isCountdownActiveRef.current) return
    if (!shouldWarn || warningTimeMs <= 0) return

    isCountdownActiveRef.current = true
    setOpen(true)

    const startTime = Date.now()
    const endTime = startTime + warningTimeMs
    setRemainingTime(Math.ceil(warningTimeMs / 1000))

    const tick = () => {
      if (!isCountdownActiveRef.current) return

      const now = Date.now()
      const timeLeft = Math.max(0, Math.ceil((endTime - now) / 1000))

      setRemainingTime(timeLeft)

      if (timeLeft <= 0) {
        logout()
        return
      }

      timerRef.current = setTimeout(tick, COUNTDOWN_INTERVAL)
    }

    tick() // Start immediately
  }, [logout, shouldWarn, warningTimeMs])

  const scheduleWarning = useCallback(() => {
    if (timeoutInMs == null) return

    clearAllTimers()
    // T = 0 → disabled entirely
    if (timeoutInMs === 0) return

    const idleDuration = Date.now() - lastActivityRef.current

    if (idleDuration >= timeoutInMs) {
      logout()
      return
    }

    if (!shouldWarn) {
      // No warning window (T < 2m): schedule direct logout at T
      const timeToLogout = timeoutInMs - idleDuration
      if (timeToLogout <= 0) {
        logout()
      } else {
        timerRef.current = setTimeout(logout, timeToLogout)
      }
      return
    }

    const timeToWarning = timeoutInMs - warningTimeMs - idleDuration

    if (timeToWarning <= 0) {
      startCountdown()
    } else {
      timerRef.current = setTimeout(startCountdown, timeToWarning)
    }
  }, [timeoutInMs, shouldWarn, warningTimeMs, startCountdown, clearAllTimers, logout])

  const handleContinue = useCallback(() => {
    setOpen(false)
    clearAllTimers()
    updateActivity()
    scheduleWarning()
  }, [updateActivity, scheduleWarning, clearAllTimers])

  // Optimized throttled activity handler
  const activityHandler = useCallback(() => {
    // 1) Don’t touch anything if the modal’s open, countdown active, or no timeout configured
    if (open || isCountdownActiveRef.current || timeoutInMs == null || timeoutInMs === 0) return
    if (throttleRef.current) return

    const now = Date.now()
    const timeSinceLastUpdate = now - lastActivityRef.current

    if (timeSinceLastUpdate > THROTTLE_INTERVAL) {
      updateActivity()
      scheduleWarning()
    }

    throttleRef.current = setTimeout(() => {
      throttleRef.current = null
    }, THROTTLE_INTERVAL)
  }, [open, updateActivity, scheduleWarning, timeoutInMs])

  // Optimized cross-tab synchronization
  const handleStorageChange = useCallback(
    (e: StorageEvent) => {
      if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
        const newActivity = parseInt(e.newValue, 10)
        if (!isNaN(newActivity) && newActivity > lastActivityRef.current) {
          lastActivityRef.current = newActivity
          if (open) {
            setOpen(false)
            clearAllTimers()
          }
          scheduleWarning()
        }
      }
    },
    [scheduleWarning, open, clearAllTimers, LAST_ACTIVITY_KEY]
  )

  // Initialize and setup event listeners
  useEffect(() => {
    if (timeoutInMs == null || timeoutInMs === 0) return // disabled or not configured
    const savedActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
    if (savedActivity) {
      lastActivityRef.current = parseInt(savedActivity, 10)
    } else {
      updateActivity()
    }

    scheduleWarning()

    // Optimized event listener setup
    const eventOptions = { passive: true, capture: false }

    activityEvents.forEach((event) => {
      window.addEventListener(event, activityHandler, eventOptions)
    })

    window.addEventListener('storage', handleStorageChange)

    // Cleanup function
    return () => {
      clearAllTimers()
      isCountdownActiveRef.current = false
      activityEvents.forEach((event) => {
        window.removeEventListener(event, activityHandler)
      })
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [activityHandler, handleStorageChange, scheduleWarning, updateActivity, clearAllTimers, activityEvents, timeoutInMs, LAST_ACTIVITY_KEY])

  // Format time display
  const timeDisplay = useMemo(() => {
    const minutes = Math.floor(remainingTime / 60)
    const seconds = remainingTime % 60
    const totalWarningTime = warningTimeMs / 1000 || 1
    const progress = (remainingTime / totalWarningTime) * 100

    const formattedTime = minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, '0')}` : `${seconds}`

    const timeUnit = remainingTime === 1 ? 'second' : minutes > 0 ? 'minutes' : 'seconds'
    const isUrgent = remainingTime <= 30 // Last 30 seconds

    return {
      formattedTime,
      timeUnit,
      progress,
      isUrgent,
    }
  }, [remainingTime, warningTimeMs])

  // Only render modal when open
  if (!open) return null

  // disable backdrop click
  const handleClosePopup = (_: React.MouseEvent<HTMLElement>, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      handleContinue()
    }
  }

  return (
    <Dialog open={open} onClose={handleClosePopup} role='dialog' aria-labelledby='session-timeout-dialog' aria-describedby='session-timeout-description' maxWidth='sm' fullWidth>
      <div className={modalStyles.dialogHeader}>
        <h3>Session Timeout</h3>
      </div>

      <div className={modalStyles.dialogContent}>
        {/* Simple Circular Timer */}
        <Box display='flex' flexDirection='column' alignItems='center' sx={{ mb: 3 }}>
          <Box position='relative' display='inline-flex' alignItems='center' justifyContent='center'>
            <CircularProgress
              variant='determinate'
              value={timeDisplay.progress}
              size={120}
              thickness={4}
              sx={{
                color: timeDisplay.isUrgent ? '#c03403' : '#8FB3DC',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                },
              }}
            />
            <Box position='absolute' display='flex' flexDirection='column' alignItems='center' justifyContent='center'>
              <Typography
                variant='h4'
                component='div'
                sx={{
                  fontWeight: 'bold',
                  fontFamily: 'monospace',
                  fontSize: '1.8rem',
                  lineHeight: 1,
                }}
              >
                {timeDisplay.formattedTime}
              </Typography>
              <Typography
                variant='caption'
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  mt: 0.5,
                }}
              >
                {timeDisplay.timeUnit}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography
          variant='body1'
          sx={{
            textAlign: 'center',
            mb: 2,
            fontSize: '16px',
            lineHeight: '24px',
          }}
        >
          Your session will expire in{' '}
          <strong>
            {timeDisplay.formattedTime} {timeDisplay.timeUnit}
          </strong>{' '}
          due to inactivity.
        </Typography>

        <Typography
          variant='body1'
          sx={{
            textAlign: 'center',
            mb: 2,
            fontSize: '16px',
            lineHeight: '24px',
          }}
        >
          You will be automatically logged out to protect your account security. Click "Continue" to extend your session.
        </Typography>

        <Typography
          variant='body1'
          sx={{
            textAlign: 'center',
            fontSize: '16px',
            lineHeight: '24px',
          }}
        >
          If you're done working, you can click "Logout Now" to log out immediately.
        </Typography>
      </div>

      <div className={modalStyles.dialogActions}>
        <LxCancelButton onCancel={logout} classNames='lxActionButton,lxActionButtonDefaultSize' label='Logout Now' />
        <LxSaveButton onSubmit={handleContinue} classNames='lxActionButton,lxActionButtonFilled,lxActionButtonDefaultSize' label='Continue' />
      </div>

      {/* Remove CSS animation styles */}
    </Dialog>
  )
}

export default SessionTimeout
